import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';

const BrandMentions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const includes = [
    {
      title: "Relevant Niche Sites",
      desc: "Mentions on websites that actually matter to your specific industry.",
      icon: FiIcons.FiTarget
    },
    {
      title: "Contextual Placement",
      desc: "Your brand integrated naturally within high-quality articles and discussions.",
      icon: FiIcons.FiAlignLeft
    },
    {
      title: "Industry Alignment",
      desc: "Positioning that ensures your brand is seen by the right target audience.",
      icon: FiIcons.FiBriefcase
    },
    {
      title: "Organic Integration",
      desc: "Mentions that feel like a natural part of the content, not an advertisement.",
      icon: FiIcons.FiZap
    },
    {
      title: "Long-term Visibility",
      desc: "Structured campaigns designed for persistent brand presence across the web.",
      icon: FiIcons.FiLayers
    }
  ];

  const benefits = [
    "Increase brand awareness",
    "Strengthen credibility and trust",
    "Support search engine rankings",
    "Build a consistent online presence",
    "Complement link building and PR"
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Hero Section - H1 */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight"
            >
              Brand Mentions <span className="text-pink-500">Services</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto"
            >
              Increase your brand visibility, build trust, and strengthen your online presence with strategic brand mentions across relevant websites.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center w-full sm:w-auto"
            >
              <Button href="https://app.prboost.io/" variant="primary" className="!from-pink-600 !to-rose-600 !px-12 !py-5 text-xl font-bold shadow-[0_0_40px_rgba(219,39,119,0.4)] w-full sm:w-auto">
                Start Building Presence
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Authority Section - H2 */}
      <section className="py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          
          <div className="max-w-4xl mx-auto text-center mb-24">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Build Authority with Strategic Brand Mentions</h2>
            <div className="text-slate-400 space-y-8 text-xl leading-relaxed mx-auto">
              <p>
                Brand mentions are a key signal for both search engines and users. Even without a direct link, being mentioned on relevant and trusted websites helps reinforce your brand’s authority and credibility.
              </p>
              <p>
                PRBoost helps businesses and agencies secure brand mentions across real websites, positioning your brand in the right context and in front of the right audience.
              </p>
            </div>
          </div>

          {/* Service Includes - H2 */}
          <div className="max-w-6xl mx-auto mb-32">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center tracking-tight">What This Service Includes</h2>
            <div className="flex flex-wrap justify-center gap-6">
              {includes.map((item, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] p-8 rounded-3xl glass-card border-white/5 hover:border-pink-500/20 transition-all group flex flex-col items-center text-center"
                >
                  <div className="w-14 h-14 rounded-xl bg-pink-500/10 flex items-center justify-center text-pink-400 mb-6 group-hover:scale-110 transition-transform">
                    <SafeIcon icon={item.icon} className="text-2xl" />
                  </div>
                  <h4 className="text-white font-bold text-xl mb-4">{item.title}</h4>
                  <p className="text-slate-400 leading-relaxed text-lg">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Why It Matters - H2 */}
          <div className="max-w-4xl mx-auto">
            <div className="glass-card rounded-[3rem] p-12 md:p-20 border-pink-500/20 bg-gradient-to-br from-slate-900/90 to-pink-900/10 text-center">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Why Brand Mentions Matter</h2>
              <p className="text-xl md:text-2xl text-slate-400 mb-12 leading-relaxed max-w-2xl mx-auto">
                Search engines increasingly evaluate brand presence across the web. Mentions on trusted websites help build authority and support your overall SEO performance.
              </p>
              <div className="flex justify-center">
                <ul className="space-y-6 text-left inline-flex flex-col">
                  {benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-5 text-slate-300 text-xl">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="text-pink-400 text-2xl flex-shrink-0" />
                      <span className="font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Ecosystem Section - H2 */}
      <section className="py-24 bg-slate-900/30 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Combine Mentions with PR and SEO</h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              Brand mentions are most effective when combined with other strategies such as guest posts, press releases, and link placements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="p-10 rounded-3xl glass-card border-white/5 text-center flex flex-col items-center">
              <div className="w-16 h-16 rounded-2xl bg-pink-500/10 flex items-center justify-center text-pink-400 mb-6">
                <SafeIcon icon={FiIcons.FiTrendingUp} className="text-3xl" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">The Complete Ecosystem</h4>
              <p className="text-slate-400 mb-6 text-lg leading-relaxed">
                PRBoost allows you to build a complete ecosystem where your brand appears across multiple channels, reinforcing your authority.
              </p>
            </div>
            
            <div className="p-10 rounded-3xl glass-card border-white/5 text-center flex flex-col items-center">
              <div className="w-16 h-16 rounded-2xl bg-pink-500/10 flex items-center justify-center text-pink-400 mb-6">
                <SafeIcon icon={FiIcons.FiUsers} className="text-3xl" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">Agency-Ready Scaling</h4>
              <p className="text-slate-400 mb-6 text-lg leading-relaxed">
                Manage multiple campaigns and clients. Align mentions with your SEO strategy while maintaining consistency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Last Section - H2 */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-pink-600/5 blur-[120px] rounded-full"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-7xl font-extrabold text-white mb-8 tracking-tighter">Start Building Your <span className="text-pink-500 text-gradient">Brand Presence</span></h2>
          <p className="text-xl md:text-2xl text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            A strong brand presence across the web is essential for long-term growth. Position your business in front of the right audience today.
          </p>
          <div className="flex justify-center">
            <Button href="https://app.prboost.io/" variant="primary" className="!from-pink-600 !to-rose-600 !px-16 !py-6 text-2xl font-bold shadow-[0_0_60px_rgba(219,39,119,0.3)]">
              Create an Account
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandMentions;
